import { gql } from '@apollo/client';
import {
  ApiScenario,
  IWorksheetAPIObject,
  Scenario,
} from 'spw-redux/modules/calculators/types';
import { graphqlClient } from './utils';
import { SCENARIO_AND_WORKSHEET_FIELDS, SCENARIO_FIELDS } from './fragments';

export const saveNewScenario = (scenario: Partial<ApiScenario>) =>
  graphqlClient.mutate<{
    scenario: Scenario & { worksheet: IWorksheetAPIObject };
  }>({
    mutation: gql`
      ${SCENARIO_AND_WORKSHEET_FIELDS}
      mutation SaveNewScenario($scenario: ScenarioArgs!) {
        scenario: saveScenario(scenario: $scenario) {
          ...ScenarioAndWorksheetFields
        }
      }
    `,
    variables: { scenario },
  });

export const updateScenario = (scenario: Partial<ApiScenario>) =>
  graphqlClient.mutate({
    mutation: gql`
      ${SCENARIO_FIELDS}
      mutation UpdateScenario($id: Int!, $scenario: ScenarioArgs!) {
        scenario: updateScenario(scenario: $scenario, id: $id) {
          ...ScenarioFields
        }
      }
    `,
    variables: {
      scenario: {
        ...scenario,
        calculatorType: scenario.calculatorType.replace(/-/g, '_'),
      },
      id: scenario.id,
    },
  });

export const bulkUpdateScenarios = (scenarios: Partial<ApiScenario>[]) =>
  graphqlClient.mutate({
    mutation: gql`
      ${SCENARIO_FIELDS}
      mutation BulkUpdateScenarios($scenarios: [ScenarioUpdate]!) {
        scenarios: bulkUpdateScenarios(scenarios: $scenarios) {
          ...ScenarioFields
        }
      }
    `,
    variables: {
      scenarios: scenarios.map((scenario) => ({
        id: scenario.id,
        scenario: {
          ...scenario,
          calculatorType: scenario.calculatorType.replace(/-/g, '_'),
        },
      })),
    },
  });

export const deleteScenario = (scenarioId: number) =>
  graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
      mutation DeleteScenario($scenarioId: Int!) {
        deleteScenario(id: $scenarioId) {
          id
          floorId
          buildingId
          calculatorType
        }
      }
    `,
    variables: {
      scenarioId,
    },
  });

export const getScenario = (scenarioId: number) => {
  return graphqlClient.query({
    fetchPolicy: 'no-cache',
    query: gql`
        ${SCENARIO_FIELDS}
        query GetScenario {
            scenario: getScenario(scenarioId: ${scenarioId}) {
                ...ScenarioFields
            }
        }
    `,
  });
};

export const getProjectScenarios = (projectId: number) =>
  graphqlClient.query<{ scenarios: Scenario[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      ${SCENARIO_FIELDS}
      query GetProjectScenarios($projectId: Int!) {
        scenarios: getProjectScenarios(projectId: $projectId) {
          ...ScenarioFields
        }
      }
    `,
    variables: {
      projectId,
    },
  });

export const cloneScenario = (
  scenarioId: number,
  scenario: Partial<ApiScenario>,
  shouldCopyNotes = false
) =>
  graphqlClient.mutate<{
    scenario: Scenario & { worksheet: IWorksheetAPIObject };
  }>({
    mutation: gql`
      ${SCENARIO_AND_WORKSHEET_FIELDS}
      mutation CloneScenario(
        $scenarioId: Int!
        $scenario: ScenarioArgs!
        $shouldCopyNotes: Boolean
      ) {
        scenario: cloneScenario(
          scenarioId: $scenarioId
          scenario: $scenario
          shouldCopyNotes: $shouldCopyNotes
        ) {
          ...ScenarioAndWorksheetFields
        }
      }
    `,
    variables: {
      scenarioId,
      scenario,
      shouldCopyNotes,
    },
  });

export const getScenariosUsingSpaceTypeApi = (
  projectId: number,
  spaceTypeId: number,
  isChild: boolean
) =>
  graphqlClient.query<{ scenarios: Scenario[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      ${SCENARIO_FIELDS}
      query GetScenariosUsingSpaceType(
        $projectId: Int
        $spaceTypeId: Int
        $isChild: Boolean
      ) {
        scenarios: getScenariosUsingSpaceType(
          projectId: $projectId
          spaceTypeId: $spaceTypeId
          isChild: $isChild
        ) {
          ...ScenarioFields
          department {
            id
            name
            secondaryIdentifier
          }
          worksheet {
            id
            spaceAllocation
            rows: worksheetRows {
              id
              child
              quantity
              seatCap
              sizeX
              sizeY
              usf
              ratio
              spaceId
              projectSpaceType {
                id
                previousId
                spaceType {
                  id
                  spaceType
                  name
                  isCustom
                  icon
                }
                category {
                  id
                  name
                  parentId
                  defaultSpaceTypeCategory {
                    id
                    category
                    name
                  }
                }
              }
            }
            categories {
              categoryId
              visible
            }
          }
        }
      }
    `,
    variables: {
      projectId,
      spaceTypeId,
      isChild,
    },
  });

export const getScenariosUsingCategoryApi = (spaceTypeCategoryId: number) => {
  return graphqlClient.query<{ scenarios: Scenario[] }>({
    fetchPolicy: 'no-cache',
    query: gql`
      ${SCENARIO_FIELDS}
      query ScenariosUsingCategory($spaceTypeCategoryId: ID!) {
        scenarios: scenariosUsingCategory(
          spaceTypeCategoryId: $spaceTypeCategoryId
        ) {
          ...ScenarioFields
          department {
            id
            name
            secondaryIdentifier
          }
          worksheet {
            id
            spaceAllocation
            rows: worksheetRows {
              id
              child
              quantity
              seatCap
              sizeX
              sizeY
              usf
              ratio
              spaceId
              projectSpaceType {
                id
                previousId
                spaceType {
                  id
                  spaceType
                  name
                  isCustom
                  icon
                }
                category {
                  id
                  name
                  parentId
                  defaultSpaceTypeCategory {
                    id
                    category
                    name
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      spaceTypeCategoryId,
    },
  });
};
